import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Text,
  HStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Lightbulb, Wrench } from 'lucide-react';
import { Icon } from '@chakra-ui/react';

interface AddIdeaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: { title: string; description: string; type: 'idea' | 'fix' }) => Promise<void>;
}

const validationSchema = Yup.object({
  title: Yup.string()
    .required('Title is required')
    .min(3, 'Title must be at least 3 characters')
    .max(100, 'Title must be less than 100 characters'),
  description: Yup.string()
    .required('Description is required')
    .min(10, 'Description must be at least 10 characters')
    .max(500, 'Description must be less than 500 characters'),
  type: Yup.string().oneOf(['idea', 'fix']).required('Type is required'),
});

/**
 * Add Idea Modal Component
 * Form for creating new ideas or fixes
 */
export function AddIdeaModal({ isOpen, onClose, onSubmit }: AddIdeaModalProps) {
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <Formik
          initialValues={{
            title: '',
            description: '',
            type: 'idea' as 'idea' | 'fix',
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              await onSubmit(values);
              resetForm();
              onClose();
            } catch (error) {
              console.error('Error submitting idea:', error);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, errors, touched, values, setFieldValue }) => {
            const selectedType = values.type;
            
            return (
              <Form>
                <ModalHeader fontWeight="bold">Submit an Idea or Feedback</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="medium" mb={2}>
                        Type
                      </FormLabel>
                      <HStack spacing={3}>
                        <Button
                          type="button"
                          flex={1}
                          leftIcon={<Icon as={Lightbulb} w={4} h={4} />}
                          onClick={() => setFieldValue('type', 'idea')}
                          bg={selectedType === 'idea' ? 'purple.100' : bgColor}
                          color={selectedType === 'idea' ? 'purple.700' : textColor}
                          borderWidth={1}
                          borderColor={selectedType === 'idea' ? 'purple.300' : borderColor}
                          _hover={{
                            bg: selectedType === 'idea' ? 'purple.200' : 'gray.50',
                          }}
                          variant={selectedType === 'idea' ? 'solid' : 'outline'}
                        >
                          New Idea
                        </Button>
                        <Button
                          type="button"
                          flex={1}
                          leftIcon={<Icon as={Wrench} w={4} h={4} />}
                          onClick={() => setFieldValue('type', 'fix')}
                          bg={selectedType === 'fix' ? 'red.100' : bgColor}
                          color={selectedType === 'fix' ? 'red.700' : textColor}
                          borderWidth={1}
                          borderColor={selectedType === 'fix' ? 'red.300' : borderColor}
                          _hover={{
                            bg: selectedType === 'fix' ? 'red.200' : 'gray.50',
                          }}
                          variant={selectedType === 'fix' ? 'solid' : 'outline'}
                        >
                          Needs Fix
                        </Button>
                      </HStack>
                    </FormControl>
                    <FormControl isRequired isInvalid={!!errors.title && touched.title}>
                      <FormLabel fontSize="sm" fontWeight="medium">
                        Title
                      </FormLabel>
                      <Field as={Input} name="title" placeholder="Brief description..." />
                      {errors.title && touched.title && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.title}
                        </Text>
                      )}
                    </FormControl>
                    <FormControl isRequired isInvalid={!!errors.description && touched.description}>
                      <FormLabel fontSize="sm" fontWeight="medium">
                        Details
                      </FormLabel>
                      <Field
                        as={Textarea}
                        name="description"
                        placeholder="Provide more details about your idea or what needs to be fixed..."
                        rows={6}
                        resize="vertical"
                      />
                      {errors.description && touched.description && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.description}
                        </Text>
                      )}
                    </FormControl>
                  </VStack>
                </ModalBody>
                <ModalFooter>
                  <Button variant="ghost" mr={3} onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    bg="purple.500"
                    color="white"
                    _hover={{ bg: 'purple.600' }}
                    isLoading={isSubmitting}
                  >
                    Submit
                  </Button>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </ModalContent>
    </Modal>
  );
}

